import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';

import { trackEvent } from '@xingternal/hops-tracking';

import { showNotification } from '@/ducks/notification';
import { fetchJson } from '@/helpers/network';
import { prepareQuery } from '@/helpers/search-helpers';
import { SearchAlertQuery } from '@/types';

import { RootState } from '.';

type TrackingEvent = {
  eventName: string;
  context: {
    PropTrackAction: string;
    PropActionOrigin: string;
  };
};

type AlertPayload = {
  alert: {
    email: string;
    query: SearchAlertQuery;
  };
};

const trackOnError = (payload, trackingConfig) => {
  const status = payload?.response?.status;
  const errorContext = {
    PropTrackAction: `${trackingConfig.context.PropTrackAction}_error`,
    PropActionOrigin: `${trackingConfig.context.PropActionOrigin}_error${status ? `_${status}` : ''}`,
  };

  trackEvent(trackingConfig.eventName, errorContext);
};

export const LOGGED_OUT_ENDPOINT = '/jobs/api/saved_searches';

export type CreateSearchAlertParams = {
  email?: string;
  query?: SearchAlertQuery;
  trackingConfig?: TrackingEvent;
};

export const createLoggedOutSearchAlert =
  ({ email, query, trackingConfig }: CreateSearchAlertParams = {}): ThunkAction<
    Promise<AlertPayload>,
    RootState,
    unknown,
    AnyAction
  > =>
  (dispatch, getState): Promise<AlertPayload> => {
    const body = JSON.stringify({
      alert: {
        email,
        query: prepareQuery(query ?? getState().search.form),
      },
    });

    return fetchJson(LOGGED_OUT_ENDPOINT, { method: 'POST', body })
      .then((payload: AlertPayload) => {
        trackingConfig && trackEvent(trackingConfig.eventName, trackingConfig.context);
        dispatch(
          showNotification({
            type: 'confirmation',
            message: email
              ? 'JOBS_CREATE_SEARCH_ALERT_LIGHTBOX_SUBSCRIPTION_CONFIRM'
              : 'JOBS_LOGGED_IN_CREATE_SUCCESS_TITLE',
          })
        );

        return payload;
      })
      .catch((payload) => {
        trackingConfig && trackOnError(payload, trackingConfig);

        const alreadyExistsError = payload?.jsonResponse?.error_name === 'ALREADY_EXISTS';

        dispatch(
          showNotification({
            type: 'error',
            message: alreadyExistsError ? 'SE_AGENTS_JOBS_ALERT_DUPLICATED_TITLE' : 'SE_GENERIC_ERROR_BODY',
          })
        );

        if (!alreadyExistsError) throw payload;
      });
  };
